import React, { useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import type PAGE_TITLES from '../../consts/titles'
import NoPermissionsFeature from '../../features/NoPermissionsFeature'
import { getPagePermissions, getLoading } from '../../store/slices/roleSlice/selectors'
import { useAppSelector } from '../../store'
import { preparedRoutes } from '../../hooks/useAllowedPath'
import ROUTES from '../../consts/paths'
import { useUserProfile } from '../../hooks/useUserProfile'
import { CURRENT_ROLE_ID_KEY } from '../../consts/roles'
import { getPriorityBaseRoute } from '../../helpers/navigationHelpers'

export interface PageProps {
  title?: PAGE_TITLES
}

const Page: React.FC<PageProps> = ({ title }): JSX.Element => {
  const navigate = useNavigate()
  const location = useLocation()
  const pagePermissions = getPagePermissions()
  const permissionLoading = useAppSelector(getLoading)
  const hasRedirected = useRef(false)
  const { data: userProfile } = useUserProfile()

  useEffect(() => {
    // Prevent multiple redirects
    if (hasRedirected.current) {
      return
    }

    // Wait for permissions to finish loading before checking
    if (!permissionLoading) {
      // Check if user has permissions - if they do, redirect them away
      if (pagePermissions && Array.isArray(pagePermissions) && pagePermissions?.length > 0) {
        hasRedirected.current = true
        const validPaths = pagePermissions?.flatMap(page => preparedRoutes[page]).filter(Boolean)
        const uniqueValidPaths = [...new Set(validPaths)]
        const currentRoleId = localStorage.getItem(CURRENT_ROLE_ID_KEY)
        const currentRole = userProfile?.roles?.find(role => role.roleId === currentRoleId)
        const priorityBaseRoute = getPriorityBaseRoute(currentRole?.roleName, uniqueValidPaths)

        if (priorityBaseRoute) {
          navigate(priorityBaseRoute, { replace: true })
        } else {
          // Fallback to home if no valid route found
          navigate(ROUTES.HOME, { replace: true })
        }
      }
    }
  }, [pagePermissions, permissionLoading, navigate, userProfile?.roles])

  // Reset redirect flag if pathname changes (user navigated away)
  useEffect(() => {
    if (location.pathname !== ROUTES.PERMISSION_DENIED) {
      hasRedirected.current = false
    }
  }, [location.pathname])

  return (
    <NoPermissionsFeature title={title} />
  )
}

export default Page
